import { useState, useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { EntityCard, FloatingActionButton, GlassPanel, Icon, LimitUpgradePrompt } from '../components/common'
import { EntityModal } from '../components/EntityModal'
import { useLocations, useLocationOperations } from '../hooks/useFirestore'
import { useProject } from '../context/ProjectContext'
import { useLimits } from '../hooks/useLimits'
import { deleteFiles, extractPublicIdFromUrl } from '../services/storage'
import { updateBidirectionalLinks, removeBidirectionalLinksOnDelete } from '../services/bidirectionalLinks'
import { COLLECTIONS } from '../services/firestore'
import './LocationsView.css'

// LocalStorage key for tier filter persistence
const TIER_FILTER_KEY = 'tierFilters_locations'

/**
 * Helper to get image URL from either string or object format
 * Supports both legacy (string) and new ({ url, publicId }) formats
 */
const getImageUrl = (image) => {
  if (!image) return ''
  if (typeof image === 'string') return image
  return image.url || ''
}

/**
 * Helper to collect all image publicIds from an entity
 * Returns array of publicIds for deletion
 */
const collectImagePublicIds = (entity) => {
  const publicIds = []
  
  // Primary image
  if (entity.primaryImage) {
    if (typeof entity.primaryImage === 'object' && entity.primaryImage.publicId) {
      publicIds.push(entity.primaryImage.publicId)
    } else if (typeof entity.primaryImage === 'string') {
      // Try to extract from URL for legacy data
      const extracted = extractPublicIdFromUrl(entity.primaryImage)
      if (extracted) publicIds.push(extracted)
    }
  }
  
  // Extra images
  if (entity.extraImages && Array.isArray(entity.extraImages)) {
    for (const img of entity.extraImages) {
      if (typeof img === 'object' && img.publicId) {
        publicIds.push(img.publicId)
      } else if (typeof img === 'string') {
        const extracted = extractPublicIdFromUrl(img)
        if (extracted) publicIds.push(extracted)
      } else if (typeof img === 'object' && img.url) {
        const extracted = extractPublicIdFromUrl(img.url)
        if (extracted) publicIds.push(extracted)
      }
    }
  }
  
  return publicIds.filter(Boolean)
}

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Container variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

/**
 * LocationsView - Grid display of location cards with CRUD operations
 */
export default function LocationsView() {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [originRect, setOriginRect] = useState(null)
  const [limitPromptOpen, setLimitPromptOpen] = useState(false)
  const fabRef = useRef(null)

  // Tier filter state (default: A and B shown, C hidden)
  const [tierFilters, setTierFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(TIER_FILTER_KEY)
      return saved ? JSON.parse(saved) : { A: true, B: true, C: false }
    } catch {
      return { A: true, B: true, C: false }
    }
  })

  // Project context for scoped data access and permissions
  const { projectId, can } = useProject()
  const canCreate = can('create')
  
  // Limits checking
  const { canCreate: canCreateEntity, getLimit } = useLimits(projectId)

  // Firestore data and operations (project-scoped)
  const { data: locations, loading, error } = useLocations(projectId, {
    orderByField: 'createdAt',
    orderDirection: 'desc',
  })
  const locationOps = useLocationOperations(projectId)

  // Filter locations by tier
  const filteredLocations = useMemo(() => {
    if (!locations) return []
    return locations.filter(location => {
      const tier = (location.tier || 'B').toUpperCase()
      return tierFilters[tier] !== false
    })
  }, [locations, tierFilters])

  // Toggle a tier filter
  const toggleTier = (tier) => {
    const newFilters = { ...tierFilters, [tier]: !tierFilters[tier] }
    setTierFilters(newFilters)
    try {
      localStorage.setItem(TIER_FILTER_KEY, JSON.stringify(newFilters))
    } catch {
      // Ignore localStorage errors
    }
  }

  // Handle create click - check limits first
  const handleCreateClick = (event) => {
    if (!canCreateEntity('locations')) {
      setLimitPromptOpen(true)
      return
    }
    openCreateModal(event)
  }

  // Open modal in create mode
  const openCreateModal = (event) => {
    setModalMode('create')
    setSelectedLocation(null)
    if (event?.currentTarget) {
      setOriginRect(event.currentTarget.getBoundingClientRect())
    } else if (fabRef.current) {
      setOriginRect(fabRef.current.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  // Open modal in edit mode
  const openEditModal = (location, event) => {
    setModalMode('edit')
    setSelectedLocation(location)
    if (event?.currentTarget) {
      setOriginRect(event.currentTarget.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  // Handle save (create or update)
  const handleSave = async (data) => {
    try {
      if (modalMode === 'create') {
        const newId = await locationOps.add(data)
        // Update bidirectional links for the new entity
        await updateBidirectionalLinks(projectId, COLLECTIONS.LOCATIONS, newId, null, data)
      } else {
        await locationOps.update(selectedLocation.id, data)
        // Update bidirectional links (compare old vs new links)
        await updateBidirectionalLinks(projectId, COLLECTIONS.LOCATIONS, selectedLocation.id, selectedLocation, data)
      }
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to save location:', err)
      throw err
    }
  }

  // Handle delete (also deletes associated Cloudinary images and cleans up bidirectional links)
  const handleDelete = async () => {
    try {
      // Collect all image publicIds from the location
      const publicIds = collectImagePublicIds(selectedLocation)
      
      // Delete images from Cloudinary (don't fail if this fails)
      if (publicIds.length > 0) {
        console.log('Deleting Cloudinary images:', publicIds)
        const deleteResult = await deleteFiles(publicIds)
        if (deleteResult.errors?.length > 0) {
          console.warn('Some images failed to delete:', deleteResult.errors)
        }
      }
      
      // Clean up bidirectional links before deleting
      await removeBidirectionalLinksOnDelete(projectId, COLLECTIONS.LOCATIONS, selectedLocation.id, selectedLocation)
      
      // Delete the location document from Firestore
      await locationOps.remove(selectedLocation.id)
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to delete location:', err)
      throw err
    }
  }

  // Build card details from location data
  const getCardDetails = (location) => {
    const details = []
    
    if (location.era) {
      details.push({ 
        label: 'Era', 
        value: location.era.slice(0, 20) + (location.era.length > 20 ? '...' : '')
      })
    }
    
    if (location.keyElements) {
      details.push({ 
        label: 'Elements', 
        value: location.keyElements.slice(0, 20) + (location.keyElements.length > 20 ? '...' : '')
      })
    }
    
    if (location.atmosphereNotes) {
      details.push({ 
        label: 'Atmosphere', 
        value: location.atmosphereNotes.slice(0, 20) + (location.atmosphereNotes.length > 20 ? '...' : '')
      })
    }
    
    if (location.linkedCharacters?.length > 0) {
      details.push({ 
        label: 'Characters', 
        value: location.linkedCharacters.length.toString()
      })
    }
    
    return details.slice(0, 4)
  }

  // Get subtitle from description
  const getSubtitle = (location) => {
    if (!location.description) return 'No description'
    return location.description.slice(0, 50) + (location.description.length > 50 ? '...' : '')
  }

  return (
    <motion.div 
      className="locations-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="locations-view__header">
        <div className="locations-view__title-group">
          <span className="locations-view__icon"><Icon name="locations" size={28} /></span>
          <h1 className="locations-view__title">Locations</h1>
        </div>
        <p className="locations-view__subtitle">
          {loading ? 'Loading...' : `${filteredLocations.length} location${filteredLocations.length !== 1 ? 's' : ''}`}
          {!loading && locations.length !== filteredLocations.length && ` (${locations.length} total)`}
        </p>
      </header>

      {/* Tier Filter Bar */}
      {!loading && !error && locations.length > 0 && (
        <div className="locations-view__filters">
          <div className="locations-view__tier-filters">
            <span className="locations-view__filter-label">Tier:</span>
            {['A', 'B', 'C'].map(tier => (
              <button
                key={tier}
                className={`locations-view__tier-btn locations-view__tier-btn--${tier.toLowerCase()} ${tierFilters[tier] ? 'locations-view__tier-btn--active' : ''}`}
                onClick={() => toggleTier(tier)}
                title={`${tierFilters[tier] ? 'Hide' : 'Show'} Tier ${tier} locations`}
              >
                {tier}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="locations-view__error">
          <h3>Error loading locations</h3>
          <p>{error.message}</p>
        </GlassPanel>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="locations-view__loading">
          <div className="locations-view__spinner" />
          <p>Loading locations...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && locations.length === 0 && (
        <GlassPanel glow="cyan" padding="xl" className="locations-view__empty">
          <div className="locations-view__empty-icon"><Icon name="locations" size={48} /></div>
          <h2>No locations yet</h2>
          <p>{canCreate ? 'Create your first location to start building your world.' : 'No locations have been added to this project yet.'}</p>
        </GlassPanel>
      )}

      {/* No Results State (when filters hide all) */}
      {!loading && !error && locations.length > 0 && filteredLocations.length === 0 && (
        <div className="locations-view__no-results">
          <div className="locations-view__no-results-icon"><Icon name="search" size={48} /></div>
          <h3>No matching locations</h3>
          <p>Try adjusting your tier filters to see more locations.</p>
        </div>
      )}

      {/* Location Grid */}
      {!loading && !error && filteredLocations.length > 0 && (
        <motion.div 
          className="locations-view__grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredLocations.map((location, index) => (
            <EntityCard
              key={location.id}
              type="location"
              name={location.name}
              image={getImageUrl(location.primaryImage)}
              subtitle={getSubtitle(location)}
              details={getCardDetails(location)}
              tier={location.tier}
              index={index}
              onClick={(e) => openEditModal(location, e)}
            />
          ))}
        </motion.div>
      )}

      {/* Floating Action Button - only shown if user can create */}
      {canCreate && (
        <div ref={fabRef}>
          <FloatingActionButton
            icon={<Icon name="plus" size={24} />}
            variant="primary"
            size="lg"
            position="bottom-right"
            label="Add new location"
            onClick={handleCreateClick}
          />
        </div>
      )}

      {/* Entity Modal */}
      <EntityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        entityType="location"
        mode={modalMode}
        initialData={selectedLocation}
        onSave={handleSave}
        onDelete={modalMode === 'edit' ? handleDelete : undefined}
        originRect={originRect}
      />

      {/* Limit Upgrade Prompt */}
      <LimitUpgradePrompt
        isOpen={limitPromptOpen}
        onClose={() => setLimitPromptOpen(false)}
        entityType="locations"
        limit={getLimit('locations')}
      />
    </motion.div>
  )
}

